import React, { useState } from 'react';
import {
  ArrowLeft,
  RefreshCw,
  Users,
  LogOut,
  Store as StoreIcon,
  TrendingUp,
} from 'lucide-react';
import {
  registerSeller,
  getSellers,
  updateSellerPasswordByAdmin,
  deleteSellerAccount,
} from '../../api/auth';
import { GrowthTab } from './GrowthTab';
import {
  UserProfile,
  OrderSheet,
  Product,
  Store,
  Order,
  SellerAccount,
} from '../../types';
import { MasterStoresTab } from './MasterStoresTab';
import { MasterCustomersTab } from './MasterCustomersTab';

interface MasterDashboardProps {
  onExit: () => void;
  onLogout?: () => void;
  user?: UserProfile;
  orderSheets: OrderSheet[];
  onAddOrderSheet: (sheet: OrderSheet) => void;
  onUpdateOrder?: (order: Order) => void;
  onRefreshProducts?: () => void;
  onUpdateStore?: (store: Store) => void;
  stores: Store[];
  orders: Order[];
  onViewOrders: (storeId: string) => void;
  products: Product[];
  onAddProduct: (product: Product) => void;
  onUpdateProduct: (product: Product) => void;
  customerAliases?: Record<string, string>;
  onUpdateAlias?: (phone: string, nick: string) => Promise<void>;
  onImpersonate?: (userId: string) => void;
}

const MasterDashboard: React.FC<MasterDashboardProps> = ({
  onExit,
  onLogout,
  user,
  stores,
  orders,
  onUpdateStore,
  onViewOrders,
  orderSheets,
  onAddOrderSheet,
  onUpdateOrder,
  onRefreshProducts,
  products,
  onAddProduct,
  onUpdateProduct,
  customerAliases,
  onUpdateAlias,
  onImpersonate
}) => {
  const [activeTab, setActiveTab] = useState<'ACCOUNTS' | 'STORES' | 'GROWTH' | 'CUSTOMERS'>('ACCOUNTS');
  const [newSellerId, setNewSellerId] = useState('');
  const [newSellerPw, setNewSellerPw] = useState('');
  const [sellers, setSellers] = useState<SellerAccount[]>([]);
  const [sellerPasswordDrafts, setSellerPasswordDrafts] = useState<Record<string, string>>({});
  const [updatingSellerId, setUpdatingSellerId] = useState<string | null>(null);
  const [deletingSellerId, setDeletingSellerId] = useState<string | null>(null);

  const refreshSellers = React.useCallback(async () => {
    const data = await getSellers();
    setSellers(data);
  }, []);

  React.useEffect(() => {
    refreshSellers().catch(console.error);
  }, [refreshSellers]);

  const getErrorMessage = (error: any, fallback: string) => {
    const message = error?.response?.data?.message;
    if (Array.isArray(message)) return message[0] || fallback;
    if (typeof message === 'string') return message;
    return fallback;
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 font-sans relative">
      <div className="bg-gray-900 text-white px-6 pt-12 pb-6 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onExit} className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"><ArrowLeft size={18} /></button>
          <h1 className="text-xl font-black">마스터 대시보드</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => window.location.reload()} className="p-2 bg-white/10 rounded-full"><RefreshCw size={18} /></button>
          <button onClick={onLogout} className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"><LogOut size={18} /></button>
        </div>
      </div>

      <div className="flex bg-white border-b px-2 sticky top-0 z-40 shrink-0">
        <button
          onClick={() => setActiveTab('ACCOUNTS')}
          className={`flex-1 py-4 flex flex-col items-center gap-1 transition-all border-b-2 ${activeTab === 'ACCOUNTS' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
        >
          <Users size={22} />
          <span className="text-[10px] font-black uppercase tracking-tighter">계정</span>
        </button>
        <button
          onClick={() => setActiveTab('STORES')}
          className={`flex-1 py-4 flex flex-col items-center gap-1 transition-all border-b-2 ${activeTab === 'STORES' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
        >
          <StoreIcon size={22} />
          <span className="text-[10px] font-black uppercase tracking-tighter">배송지</span>
        </button>
        <button
          onClick={() => setActiveTab('GROWTH')}
          className={`flex-1 py-4 flex flex-col items-center gap-1 transition-all border-b-2 ${activeTab === 'GROWTH' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
        >
          <TrendingUp size={22} />
          <span className="text-[10px] font-black uppercase tracking-tighter">성장분석</span>
        </button>
        <button
          onClick={() => setActiveTab('CUSTOMERS')}
          className={`flex-1 py-4 flex flex-col items-center gap-1 transition-all border-b-2 ${activeTab === 'CUSTOMERS' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
        >
          <Users size={22} />
          <span className="text-[10px] font-black uppercase tracking-tighter">고객관리</span>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50/50">
        {activeTab === 'ACCOUNTS' && user?.role === 'ADMIN' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Users size={20} /> 새 판매자 계정 생성
              </h3>
              <div className="flex flex-col gap-4 max-w-sm">
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">아이디</label>
                  <input
                    type="text"
                    value={newSellerId}
                    onChange={(e) => setNewSellerId(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-emerald-500 outline-none"
                    placeholder="판매자 아이디"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">비밀번호</label>
                  <input
                    type="password"
                    value={newSellerPw}
                    onChange={(e) => setNewSellerPw(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-emerald-500 outline-none"
                    placeholder="비밀번호"
                  />
                </div>
                <button
                  onClick={async () => {
                    if (!newSellerId || !newSellerPw) return alert('아이디와 비밀번호를 입력하세요.');
                    try {
                      await registerSeller(newSellerId, newSellerPw);
                      await refreshSellers();
                      alert(`판매자 ${newSellerId} 계정이 생성되었습니다.`);
                      setNewSellerId('');
                      setNewSellerPw('');
                    } catch (e) {
                      console.error(e);
                      alert(getErrorMessage(e, '계정 생성에 실패했습니다. 이미 존재하는 아이디일 수 있습니다.'));
                    }
                  }}
                  className="h-10 bg-emerald-500 text-white font-bold rounded-lg hover:bg-emerald-600"
                >
                  계정 생성
                </button>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <div className="flex items-center justify-between gap-4 mb-4">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Users size={20} /> 판매자 계정 관리
                </h3>
                <span className="px-3 py-1 rounded-full bg-gray-100 text-xs font-bold text-gray-600">
                  총 {sellers.length}명
                </span>
              </div>

              <p className="text-sm text-gray-500 mb-6">
                비밀번호를 즉시 변경하거나 계정을 삭제할 수 있습니다. 계정 삭제 시 해당 판매자의 상품은 비활성화되고, 소유 매장은 운영자 연결이 해제됩니다.
              </p>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {sellers.map((seller) => (
                  <div key={seller.id} className="border border-gray-200 rounded-2xl p-5 bg-gray-50/70">
                    <div className="mb-4">
                      <div className="text-lg font-black text-gray-900">{seller.username}</div>
                      <div className="mt-1 text-sm text-gray-500">
                        {seller.businessName || '상호명 미설정'}
                        {seller.storeContact ? ` · ${seller.storeContact}` : ''}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">새 비밀번호</label>
                        <input
                          type="password"
                          value={sellerPasswordDrafts[seller.id] || ''}
                          onChange={(e) => setSellerPasswordDrafts((current) => ({
                            ...current,
                            [seller.id]: e.target.value,
                          }))}
                          className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-emerald-500 outline-none bg-white"
                          placeholder="4자 이상 입력"
                        />
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          disabled={updatingSellerId === seller.id}
                          onClick={async () => {
                            const newPassword = (sellerPasswordDrafts[seller.id] || '').trim();
                            if (newPassword.length < 4) {
                              return alert('새 비밀번호는 4자 이상 입력하세요.');
                            }

                            try {
                              setUpdatingSellerId(seller.id);
                              await updateSellerPasswordByAdmin(seller.id, newPassword);
                              setSellerPasswordDrafts((current) => ({
                                ...current,
                                [seller.id]: '',
                              }));
                              alert(`${seller.username} 계정의 비밀번호를 변경했습니다.`);
                            } catch (e) {
                              console.error(e);
                              alert(getErrorMessage(e, '비밀번호 변경에 실패했습니다.'));
                            } finally {
                              setUpdatingSellerId((current) => (current === seller.id ? null : current));
                            }
                          }}
                          className="flex-1 h-10 bg-gray-900 text-white font-bold rounded-lg hover:bg-black disabled:opacity-50"
                        >
                          {updatingSellerId === seller.id ? '변경 중...' : '비밀번호 변경'}
                        </button>
                        <button
                          disabled={deletingSellerId === seller.id}
                          onClick={async () => {
                            const confirmed = window.confirm(
                              `${seller.username} 계정을 삭제하시겠습니까?\n관련 상품은 비활성화되고, 소유 매장은 운영자 연결이 해제됩니다.`,
                            );
                            if (!confirmed) return;

                            try {
                              setDeletingSellerId(seller.id);
                              await deleteSellerAccount(seller.id);
                              await refreshSellers();
                              setSellerPasswordDrafts((current) => {
                                const next = { ...current };
                                delete next[seller.id];
                                return next;
                              });
                              alert(`${seller.username} 계정을 삭제했습니다.`);
                            } catch (e) {
                              console.error(e);
                              alert(getErrorMessage(e, '계정 삭제에 실패했습니다.'));
                            } finally {
                              setDeletingSellerId((current) => (current === seller.id ? null : current));
                            }
                          }}
                          className="flex-1 h-10 bg-red-50 text-red-600 font-bold rounded-lg hover:bg-red-100 disabled:opacity-50"
                        >
                          {deletingSellerId === seller.id ? '삭제 중...' : '계정 삭제'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {sellers.length === 0 && (
                  <div className="xl:col-span-2 border border-dashed border-gray-200 rounded-2xl p-8 text-center text-sm text-gray-400">
                    등록된 판매자 계정이 없습니다.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'STORES' && (
          <MasterStoresTab
            stores={stores}
            orders={orders}
            onUpdateStore={onUpdateStore}
            selectedOrderSheetId="all"
            isAdmin={true}
            onViewOrders={onViewOrders}
            sellers={sellers}
          />
        )}

        {activeTab === 'GROWTH' && (
          <GrowthTab sellers={sellers} />
        )}

        {activeTab === 'CUSTOMERS' && (
          <div className="-mx-6 px-6">
            <MasterCustomersTab
              orders={orders}
              stores={stores}
              orderSheets={orderSheets}
              customerAliases={customerAliases}
              onUpdateAlias={onUpdateAlias}
              onImpersonate={onImpersonate}
              onViewHistory={(name, phone, userId) => {
                // For now just alert or implement similar to SellerDashboard if needed
                alert(`${name} 고객님의 이력 조회 기능은 준비 중입니다.`);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MasterDashboard;
